/**
 * Authentication helper functions for USL token-based authentication flows
 */

import type { TokenTypes } from '@workivate/tho-web-shared';
import { AppLogger, LogTypes } from '@workivate/tho-web-shared';

import { getStore } from 'app/hermes-redux';
import { logout } from 'state/session/actions';
import { showToast } from 'react-components/toaster-comp/state/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';

import { callAuthorizeWithToken, performAuthCheck } from './run-with-usl';

/**
 * Handles authentication with token and performs auth check
 * Shows error toast and logs out on failure
 *
 * This function is used by multiple authentication flows:
 * - X_USL_OTT: SSOX with x-usl-ott parameter
 * - USERTOKEN_AND_OTT: SSOX with userToken and OTT
 * - USERTOKEN: Deeplink flow
 * - TOKEN: IDP initiated SSO
 *
 * @param token - The authentication token
 * @param tokenType - The type of token being used
 */
export const handleTokenAuthentication = async ({
  token,
  tokenType,
}: {
  token: string;
  tokenType: TokenTypes;
}): Promise<void> => {
  try {
    await callAuthorizeWithToken({ token, tokenType });
    await performAuthCheck();
  } catch (error) {
    AppLogger.logWithOptions({
      message: 'Error in handleTokenAuthentication',
      level: LogTypes.Error,
      error,
      shouldLogToDatadog: true,
      context: { token, tokenType },
    });

    await getStore().dispatch(logout({ isHard: false }));
    getStore().dispatch(
      showToast({
        type: ToastType.ERROR,
        message: polyglot.t('api_errors.something_wrong'),
      }),
    );
  }
};
